import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from './Button';
import Input from './Input';
import BuildBaseIcon from '../Icons/BuildBaseIcon';
import SubmitButtonLabel from './SubmitButtonLabel';
import { getFooterContent } from '../api-sanity/api';
import { submitContactForm } from '../configs/sanity';
import useMediaQuery from 'hooks/useMediaQuery';

const DEFAULT_FOOTER = {
  linksColumn: {
    title: 'LINKS',
    links: [
      { _key: 'home', label: 'HOME', linkType: 'internal', internalPath: '/' },
      {
        _key: 'about',
        label: 'ABOUT',
        linkType: 'internal',
        internalPath: '/about',
      },
      {
        _key: 'services',
        label: 'SERVICES',
        linkType: 'internal',
        internalPath: '/services',
      },
    ],
  },
  socialsColumn: {
    title: 'SOCIALS',
    links: [
      { _key: 'instagram', label: 'INSTAGRAM' },
      { _key: 'linkedin', label: 'LINKEDIN' },
      { _key: 'facebook', label: 'FACEBOOK' },
    ],
  },
  infoColumn: {
    title: 'INFO',
    address: 'SPALENRING 150\n4055 BASEL',
    email: 'INFO@BUILDBASE.CH',
  },
};

const Footer = ({ isDarkMode = false }) => {
  const [footerData, setFooterData] = useState(null);
  const isMobileSize = useMediaQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const isContactPage = location.pathname === '/contact';
  const linksColumn = footerData?.linksColumn || DEFAULT_FOOTER.linksColumn;
  const socialsColumn =
    footerData?.socialsColumn || DEFAULT_FOOTER.socialsColumn;
  const infoColumn = footerData?.infoColumn || DEFAULT_FOOTER.infoColumn;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState('idle');
  const isLocked = submitStatus === 'loading' || submitStatus === 'success';
  const [validationErrors, setValidationErrors] = useState({
    name: false,
    email: false,
    company: false,
    phone: false,
  });

  useEffect(() => {
    if (submitStatus !== 'error') return;
    const timer = setTimeout(() => setSubmitStatus('idle'), 5000);
    return () => clearTimeout(timer);
  }, [submitStatus]);

  useEffect(() => {
    const fetchFooterData = async () => {
      const data = await getFooterContent();
      if (!data.error) {
        setFooterData(data);
      }
    };
    fetchFooterData();
  }, []);

  const handleLinkClick = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizePhone = (raw) => {
    if (!raw) return '';
    const trimmed = raw.trim();
    const withPlus = trimmed.startsWith('00')
      ? `+${trimmed.slice(2)}`
      : trimmed;
    const cleaned = withPlus.replace(/(?!^)[^\d]/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    if (/^\d+$/.test(cleaned)) return `+${cleaned}`;
    return cleaned;
  };

  const isE164 = (value) => {
    return /^\+[1-9]\d{7,14}$/.test(value);
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return true;
    const normalized = normalizePhone(phone);
    return isE164(normalized);
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name.trim(),
      email: !formData.email.trim() || !validateEmail(formData.email),
      company: !formData.company.trim(),
      phone: !validatePhone(formData.phone),
    };

    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitStatus('loading');

    submitContactForm(formData)
      .then(() => {
        setSubmitStatus('success');

        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            message: '',
          });
          setValidationErrors({
            name: false,
            email: false,
            company: false,
            phone: false,
          });
          setSubmitStatus('idle');
        }, 5000);
      })
      .catch((error) => {
        console.error(error);
        setSubmitStatus('error');
      });
  };

  return (
    <div
      className={`footer-container ${isDarkMode ? 'dark' : ''} ${
        isContactPage ? 'footer-container-compact' : ''
      }`}
    >
      {!isContactPage && (
        <div className='flex footer-contact-row'>
          {!isMobileSize && (
            <BuildBaseIcon
              onClick={() => navigate('/')}
              className='footer-contact-logo cursor-pointer align-self-start'
            />
          )}
          {isMobileSize && (
            <p className='balanced-text uppercase full-width sec-nd-hp-p font-primary'>
              <span>Wie kann ich </span>
              <span className='font-secondary non-active'>sie Erreichen?</span>
            </p>
          )}
          <form className='flex footer-contact-form' onSubmit={handleSubmit}>
            <Input
              disabled={isLocked}
              placeholder='Name*'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              className={`${validationErrors.name ? 'validation-error' : ''} ${
                isLocked ? 'loading' : ''
              }`}
            />
            <Input
              disabled={isLocked}
              placeholder='E-Mail*'
              name='email'
              value={formData.email}
              onChange={handleInputChange}
              className={`${
                validationErrors.email ? 'validation-error' : ''
              } ${isLocked ? 'loading' : ''}`}
            />
            <Input
              disabled={isLocked}
              placeholder='Firmenname*'
              name='company'
              value={formData.company}
              onChange={handleInputChange}
              className={`${
                validationErrors.company ? 'validation-error' : ''
              } ${isLocked ? 'loading' : ''}`}
            />
            <Input
              disabled={isLocked}
              type='tel'
              inputMode='tel'
              autoComplete='tel'
              placeholder='Phone (e.g., +49 15123456789)'
              name='phone'
              value={formData.phone}
              onChange={handleInputChange}
              onBlur={(e) => {
                const normalized = normalizePhone(e.target.value);
                setFormData((prev) => ({ ...prev, phone: normalized }));
              }}
              pattern='^\+[1-9]\d{7,14}$'
              maxLength='16'
              title='Enter a valid number in E.164 format, e.g., +4915123456789'
              className={`${
                validationErrors.phone ? 'validation-error' : ''
              } ${isLocked ? 'loading' : ''}`}
            />
            <textarea
              disabled={isLocked}
              placeholder='Hinterlassen Sie eine Nachricht'
              name='message'
              value={formData.message}
              onChange={handleInputChange}
              maxLength='2000'
              className={isLocked ? 'loading' : ''}
            />
            <Button
              className='flex justify-center'
              type='submit'
              disabled={isLocked}
            >
              <SubmitButtonLabel status={submitStatus} />
            </Button>
          </form>
        </div>
      )}
      <div className='flex footer-links-row'>
        <div className='flex footer-links-wrapper '>
          <div className='flex footer-links'>
            {!isMobileSize && (
              <div>
                <span className='ft-tile'>{linksColumn.title}</span>
                {linksColumn.links?.map((link) => (
                  <span
                    key={link._key}
                    className='ft-btn'
                    onClick={() =>
                      link.linkType === 'external'
                        ? handleLinkClick(link.externalUrl)
                        : link.internalPath && navigate(link.internalPath)
                    }
                  >
                    {link.label}
                  </span>
                ))}
              </div>
            )}

            <div>
              <span className='ft-tile'>{socialsColumn.title}</span>
              {socialsColumn.links?.map((link) => (
                <span
                  key={link._key}
                  className='ft-btn'
                  onClick={() => handleLinkClick(link.url)}
                >
                  {link.label}
                </span>
              ))}
            </div>
            <div>
              <span className='ft-tile'>{infoColumn.title}</span>
              {infoColumn.address
                ?.split('\n')
                .filter((line) => line.trim())
                .map((line, index) => (
                  <span key={index}>{line}</span>
                ))}
              {infoColumn.email && <span>{infoColumn.email}</span>}
            </div>
          </div>
        </div>
        <div className='flex'>
          {isMobileSize && (
            <div className='logo-wrapper'>
              <BuildBaseIcon
                width='100%'
                height='100%'
                onClick={() => navigate('/')}
                className='cursor-pointer'
              />
            </div>
          )}
          <span className='footer-copyright'>
            © 2025 Build:base. All rights reserved.
          </span>
        </div>
      </div>
    </div>
  );
};

export default Footer;
